import React from "react";
import { List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { useTranslation } from "react-i18next";
import SearchRounded from "@mui/icons-material/SearchRounded";
import LocalHospitalRounded from "@mui/icons-material/LocalHospitalRounded";
import QuizRounded from "@mui/icons-material/QuizRounded";
import FlashOnRounded from "@mui/icons-material/FlashOnRounded";
import CalculateRounded from "@mui/icons-material/CalculateRounded";
import WarningRounded from "@mui/icons-material/WarningRounded";
import ScienceRounded from "@mui/icons-material/ScienceRounded";
import LibraryBooksRounded from "@mui/icons-material/LibraryBooksRounded";
import PolicyRounded from "@mui/icons-material/PolicyRounded";
import PresentToAllRounded from "@mui/icons-material/PresentToAllRounded";
import MedicalInformationRounded from "@mui/icons-material/MedicalInformationRounded";
import PsychologyRounded from "@mui/icons-material/PsychologyRounded";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";
import HealthAndSafetyRounded from "@mui/icons-material/HealthAndSafetyRounded";

// `key` names chat.suggestions.<key>.button / .message in the "assistant" namespace. The message
// is sent as the patient's own words, so it goes out in their language.
interface ChatSuggestion {
  key: string;
  icon: React.ReactNode;
}

interface ChatSuggestionsProps {
  assistantType: string;
  onSuggestionClick: (message: string) => void;
}

const getSuggestions = (assistantType: string): ChatSuggestion[] => {
  const suggestions = {
    general: [
      {
        key: "analyzeSymptoms",
        icon: <SearchRounded />,
      },
      {
        key: "findCare",
        icon: <LocalHospitalRounded />,
      },
      {
        key: "explainCondition",
        icon: <QuizRounded />,
      },
    ],
    brainflash: [
      {
        key: "calculateDosing",
        icon: <CalculateRounded />,
      },
      {
        key: "contraindications",
        icon: <WarningRounded />,
      },
      {
        key: "emergencyProtocol",
        icon: <FlashOnRounded />,
      },
    ],
    "deep-dive": [
      {
        key: "mechanisms",
        icon: <ScienceRounded />,
      },
      {
        key: "evidence",
        icon: <LibraryBooksRounded />,
      },
      {
        key: "guidelines",
        icon: <PolicyRounded />,
      },
    ],
    "peer-review": [
      {
        key: "presentCase",
        icon: <PresentToAllRounded />,
      },
      {
        key: "planTreatment",
        icon: <MedicalInformationRounded />,
      },
      {
        key: "reasoning",
        icon: <PsychologyRounded />,
      },
    ],
    "basic-medical-library": [
      {
        key: "understandCondition",
        icon: <HelpOutlineRounded />,
      },
      {
        key: "explainSymptoms",
        icon: <HealthAndSafetyRounded />,
      },
      {
        key: "tests",
        icon: <ScienceRounded />,
      },
    ],
  };

  return suggestions[assistantType] || [];
};

const ChatSuggestions: React.FC<ChatSuggestionsProps> = ({
  assistantType,
  onSuggestionClick,
}) => {
  const { t } = useTranslation("assistant");
  const suggestions = getSuggestions(assistantType);

  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  // ChatGPT-style starters: a quiet list of links under the composer, one per row.
  return (
    <List dense disablePadding sx={{ mt: 1.5, width: "100%" }}>
      {suggestions.map((suggestion, index) => (
        <ListItemButton
          key={index}
          onClick={() => onSuggestionClick(t(`chat.suggestions.${suggestion.key}.message`))}
          sx={{ borderRadius: 2, py: 1.25 }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: "text.secondary" }}>{suggestion.icon}</ListItemIcon>
          <ListItemText
            primary={t(`chat.suggestions.${suggestion.key}.button`)}
            primaryTypographyProps={{ color: "text.secondary" }}
          />
        </ListItemButton>
      ))}
    </List>
  );
};

export default ChatSuggestions;
